// Client-side ERP session, now backed by the supplier portal's backend-verified token.
// Two roles: 'admin' (team master code) and 'supplier' (per-supplier 访问码). The old
// client-side passwords (full / purchase_draft) are gone — access is verified server-side.
import {
    getSupplierSession,
    storeSupplierSession,
    clearSupplierSession,
    SupplierSession,
    SupplierRole,
} from '../supplier/supplierAccess';

export type ErpRole = 'admin' | 'supplier';

// Map the backend supplier role onto the ERP role: team → admin.
export function erpRoleFromSupplierRole(role: SupplierRole): ErpRole {
    return role === 'team' ? 'admin' : 'supplier';
}

export function getErpRole(): ErpRole | null {
    const session = getSupplierSession();
    if (!session) return null;
    return erpRoleFromSupplierRole(session.role);
}

export function isErpUnlocked(): boolean {
    return getErpRole() !== null;
}

export function storeErpSession(session: SupplierSession) {
    storeSupplierSession(session);
}

export function clearErpAccess() {
    clearSupplierSession();
}

// Public /erp pages that never require login: the per-livestream print catalog and the
// customer-facing catalog.
export function isPublicPrintPath(pathname: string): boolean {
    return /^\/erp\/live\/[^/]+\/print$/.test(pathname);
}

export function isPublicErpPath(pathname: string): boolean {
    return pathname === '/erp/catalog' || isPublicPrintPath(pathname);
}

function matchesPrefix(pathname: string, prefix: string): boolean {
    return pathname === prefix || pathname.startsWith(prefix + '/');
}

// Suppliers may only open 商品 (their own catalog) and 订单 (their orders).
const SUPPLIER_ALLOWED_PREFIXES = ['/erp/products', '/erp/orders', '/erp/profile'];
// 订单 is a supplier-only module — admins never see or open it.
const ADMIN_DENIED_PREFIXES = ['/erp/orders'];

// Client-side route gating by role. Supplier *data* is additionally enforced on the
// backend by the token; this only controls which pages the UI will render.
export function canAccessErpPath(role: ErpRole, pathname: string): boolean {
    if (role === 'admin') {
        return !ADMIN_DENIED_PREFIXES.some(prefix => matchesPrefix(pathname, prefix));
    }
    return SUPPLIER_ALLOWED_PREFIXES.some(prefix => matchesPrefix(pathname, prefix));
}

// Where each role lands right after login.
export function erpLandingPath(role: ErpRole): string {
    return role === 'admin' ? '/erp/live' : '/erp/products';
}
